import AlternateEmailOutlinedIcon from "@mui/icons-material/AlternateEmailOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import {
  Avatar,
  Box,
  Button,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import type { ListItemData } from "@entrilia/shared/components/inputs/CheckItemsList";
import CheckItemsList from "@entrilia/shared/components/inputs/CheckItemsList";
import SearchField from "@entrilia/shared/components/inputs/SearchField";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import type { OrganizationContactRole, OrganizationMember } from "../../../../api/types/userManagementTypes";
import { organizationContactRoleCaptionsMap } from "../../../../utilities/enumCaptions";
import BorderedRadioLabel from "../../../common/BorderedRadioLabel";
import type { EmailContactRow } from "./emailContactRowsValidation";
import { emptyEmailRow, validateEmailContactRows } from "./emailContactRowsValidation";
import EmailContactsEditor from "./EmailContactsEditor";
import type { OrganizationContactRoleInfo } from "./organizationContactRoles";
import type { RoleContactGroup, RoleExternal, RoleMember } from "./organizationContactsState";

type Mode = "members" | "email";

interface Props {
  roleInfo: OrganizationContactRoleInfo;
  members: OrganizationMember[];
  assigned: RoleContactGroup;
  onClose: () => void;
  onAddMembers: (role: OrganizationContactRole, members: RoleMember[]) => void;
  onAddExternals: (role: OrganizationContactRole, externals: RoleExternal[]) => void;
}

const AddOrganizationContactDialog = ({
  roleInfo,
  members,
  assigned,
  onClose,
  onAddMembers,
  onAddExternals,
}: Props) => {
  const { role, allowsExternalEmail, maxCount } = roleInfo;
  const [mode, setMode] = useState<Mode>("members");
  const [search, setSearch] = useState("");
  const [membersToAdd, setMembersToAdd] = useState<ListItemData[]>([]);
  const [emailRows, setEmailRows] = useState<EmailContactRow[]>(() => [emptyEmailRow()]);
  const [showEmailErrors, setShowEmailErrors] = useState(false);

  const remaining = maxCount - (assigned.members.length + assigned.externals.length);

  const assignedUserIds = useMemo(() => new Set(assigned.members.map((member) => member.userId)), [assigned.members]);

  const allItems = useMemo<ListItemData[]>(
    () => members.map((member) => ({ value: member.userId, label: member.name, description: member.email })),
    [members]
  );

  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return allItems;
    }
    return allItems.filter(
      (item) => item.label.toLowerCase().includes(query) || (item.description ?? "").toLowerCase().includes(query)
    );
  }, [allItems, search]);

  const assignedItems = useMemo(
    () => allItems.filter((item) => assignedUserIds.has(item.value)),
    [allItems, assignedUserIds]
  );

  const handleSelectedChanged = (selected: ListItemData[]) => {
    setMembersToAdd(selected.filter((item) => !assignedUserIds.has(item.value)));
  };

  const emailValidation = validateEmailContactRows(emailRows);

  const additionsCount = mode === "members" ? membersToAdd.length : emailValidation.contactsToAdd.length;
  const overCapacity = additionsCount > remaining;
  const canAdd = additionsCount > 0 && !overCapacity;

  const handleAdd = () => {
    if (mode === "members") {
      onAddMembers(
        role,
        membersToAdd.map((item) => ({ userId: item.value, name: item.label, email: item.description ?? "" }))
      );
      onClose();
      return;
    }

    if (!emailValidation.isValid) {
      setShowEmailErrors(true);
      return;
    }

    onAddExternals(role, emailValidation.contactsToAdd);
    onClose();
  };

  return (
    <SmallDialog open onClose={onClose} scroll="paper" sx={{ "& .MuiDialog-paper": { height: "75vh" } }}>
      <DialogTitle>Add {organizationContactRoleCaptionsMap[roleInfo.role]}</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <Divider />
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {allowsExternalEmail && (
          <FormControl>
            <RadioGroup
              row
              value={mode}
              onChange={(event) => setMode(event.target.value as Mode)}
              sx={{ flexWrap: "nowrap", gap: 1 }}
            >
              <BorderedRadioLabel<Mode>
                value="members"
                selectedValue={mode}
                sx={{ flex: 1, p: 1 }}
                label={
                  <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                    <Avatar variant="rounded" sx={{ width: 32, height: 32 }}>
                      <PeopleAltOutlinedIcon fontSize="small" color={mode === "members" ? "primary" : "action"} />
                    </Avatar>
                    <Typography>Members</Typography>
                  </Stack>
                }
              />
              <BorderedRadioLabel<Mode>
                value="email"
                selectedValue={mode}
                sx={{ flex: 1, p: 1 }}
                label={
                  <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                    <Avatar variant="rounded" sx={{ width: 32, height: 32 }}>
                      <AlternateEmailOutlinedIcon fontSize="small" color={mode === "email" ? "primary" : "action"} />
                    </Avatar>
                    <Typography>Email</Typography>
                  </Stack>
                }
              />
            </RadioGroup>
          </FormControl>
        )}

        <Stack spacing={1.5} sx={{ flex: 1, minHeight: 0, display: mode === "members" ? "flex" : "none" }}>
          <Typography color="textSecondary">Choose from existing organization users.</Typography>
          <SearchField fullWidth placeholder="Search..." debounceTimeMs={300} onSearch={setSearch} />
          <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
            <CheckItemsList
              managed
              hideSelectAll
              items={filteredItems}
              preselectedItems={assignedItems}
              disabledItems={assignedItems}
              onSelectedChanged={handleSelectedChanged}
            />
            {filteredItems.length === 0 && (
              <Typography color="textSecondary" sx={{ textAlign: "center", mt: 2 }}>
                {allItems.length === 0
                  ? "No eligible organization users are available."
                  : "No members match your search."}
              </Typography>
            )}
          </Box>
        </Stack>

        {allowsExternalEmail && (
          <Box sx={{ display: mode === "email" ? "block" : "none" }}>
            <Stack spacing={1.5}>
              <Typography color="textSecondary">
                Add an email address for shared inboxes, groups, or anyone without user access.
              </Typography>
              <EmailContactsEditor rows={emailRows} onChange={setEmailRows} showErrors={showEmailErrors} />
            </Stack>
          </Box>
        )}

        {overCapacity && (
          <FormHelperText error>
            You can add at most {remaining} more contact{remaining === 1 ? "" : "s"} to this role.
          </FormHelperText>
        )}
      </DialogContent>
      <Divider />
      <DialogButtons onCancel={onClose}>
        <Button variant="contained" disabled={!canAdd} onClick={handleAdd}>
          Add
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default AddOrganizationContactDialog;
